import { MealTunerFoodMap, MealTunerIngredient, MealTunerInvalidMeal, MealTunerMeal, MealTunerOutcome, MealTunerParams, MealTunerResults, MealTunerSolved } from './meal-tuner-interface';
import { deepClone } from './deep-helpers';
import { describeError, describeErrorForLog } from './error-describe';
const solver = require('javascript-lp-solver');
import { roundToPrecision } from './shared-util';
import { NutrientTargets, INFOODS, infoodImportanceWeights, Nutrients, calculateNutrientMatch_pc } from './data-model/food';

/**
 * The food id every typed-in ("Log Macros Manually") ingredient arrives under.
 *
 * It says what KIND of ingredient this is -- one whose nutrients the user typed in, rather
 * than one looked up in the food database. It is deliberately not unique: every typed-in
 * ingredient in every meal carries this same string, so it must never be used to tell one
 * ingredient apart from another. `modelKeyForIngredient` below is what does that.
 */
export const MACRO_INGREDIENT_FOOD_ID = 'macro-ingredient';

/**
 * The name this ingredient's variables and constraints get in the solver model.
 *
 * It is the ingredient's POSITION in the meal, never its food id. A meal is a list of
 * ingredients and not a set of foods, so two entries can share one food -- and every
 * typed-in ingredient shares `MACRO_INGREDIENT_FOOD_ID` with every other. Keying the model
 * on the food let the later entry's variables land on top of the earlier one's, so the
 * solver worked from a meal the user never logged and handed back weights computed from it.
 *
 * To the solver these names are opaque identifiers: it reads the model with `Object.keys`
 * and never orders anything by their text, so what they are called changes no result.
 */
function modelKeyForIngredient(ingredientIndex: number): string {
  return `ingredient${ingredientIndex}`;
}

/** Whether a row's amount is a number the calorie scaling can use: present, and neither NaN nor Infinity. */
function isUsableAmount(value: number): boolean {
  return value != null && isFinite(value);
}

/**
 * Whether a row is counted in WHOLE units -- in practice an egg counted by the egg. See
 * `client/src/services/food-db.service.ts`, which is what sets `fractions` to 1.
 */
function isCountedInWholeUnits(ingredient: MealTunerIngredient): boolean {
  return ingredient.fractions === 1;
}

/**
 * The rows of one meal the tuner does NOT resize, each with the amount it is held at.
 *
 * Three kinds of row are held:
 *  - a row the user locked, at its recipe amount;
 *  - a row counted in whole units whose recipe amount is below one unit, at its recipe amount;
 *  - a row counted in whole units that scaling would take below one unit, at exactly one unit.
 *
 * The last two exist because whole units cannot express less than one egg inside the
 * plus-or-minus-20% band, so resizing such a row used to round it up to one whole egg -- up to
 * twice the amount the tuner promised. The owner's decision was not to tune those rows at all
 * (df-05d8c5ad79c0).
 *
 * Kept beside the meal rather than on its rows, because the rows are handed back to the caller and
 * the caller reads `locked` as the user's own choice.
 */
type HeldRows = Map<MealTunerIngredient, number>;

/**
 * Everything one `solve` call works from, worked out once at the top of that call and handed to
 * every stage below it.
 *
 * These values used to live on the `MealTuner` object itself, which meant nothing in a stage's
 * signature said what that stage actually depended on, and a second `solve` on the same object
 * overwrote the first one's inputs. Passing them makes both of those impossible: the class holds
 * no state of its own between calls.
 */
interface MealSolveContext {
  /** the nutrients per 100 g of every food the meals name, from the caller's copied params */
  foods: MealTunerFoodMap;
  /** what this meal is being tuned towards */
  targets: NutrientTargets;
  /** the calorie target, read out of `targets` and proven to be a usable number */
  targetEnergy: number;
  /** every target's value times its importance weight, added up -- the divisor the precision uses */
  totalTargetWeight: number;
}

/**
 * The allowed amount band for one row, as the solver's model states it.
 *
 * Its unit depends on which branch `chooseFractionBand` took, so do NOT compare it against a row's
 * `tunedMeasures` without checking:
 *
 *  - the row is still counted in fractions -- the band is a count of whole FRACTION STEPS, and
 *    `gramsPerMeasure` still means grams per one measure. The model divides by the fraction count
 *    in the same places, so the two agree.
 *  - the row gave up on fractions -- the band is in GRAMS, and `gramsPerMeasure` has been set to 1
 *    so that one "measure" is one gram.
 */
interface IngredientAmountBand {
  minMeasures: number;
  maxMeasures: number;
}

/** One calorie scaling of a meal: the factor every free row is multiplied by, and what the held and the free rows weigh. */
interface ScaledTotals {
  /** multiplies every row not in `HeldRows` */
  scale: number;
  /** grams of every held row, at its held amount */
  heldWeight: number;
  /** grams of every free row BEFORE scaling */
  freeWeight: number;
}

/**
 * What `javascript-lp-solver` answers with: a value per model variable, under the same made-up
 * names the model was built with, alongside its own `feasible` flag.
 *
 * The index signature says `number` for every key, which is how this file has always read the
 * answer; `feasible` arrives as a boolean and is only ever tested for truthiness.
 */
interface SolverResults {
  [key: string]: number;
}

/**
 * The linear program handed to `javascript-lp-solver`.
 *
 * Every key in here is a name this file made up -- see `modelKeyForIngredient` and the comments
 * on the two stages that fill it. To the solver they are opaque identifiers.
 */
interface SolverModel {
  optimize: string;
  opType: string;
  constraints: { [name: string]: { [key: string]: number } };
  variables: { [name: string]: { [key: string]: number } };
  binaries: { [name: string]: number };
  ints: { [name: string]: number };
}

export class MealTuner {
  constructor() { }

  // checkIngredientsAgainstTargets(ingredients: MealTunerIngredient[], targets: NutrientTargets) {
  //   let nutrients: Nutrients = {};
  //   for (let infood of Object.keys(targets)) {
  //     nutrients[infood] = 0;
  //   }
  //   for (let ingredient of ingredients) {
  //     for (let infood of Object.keys(nutrients)) {
  //       let value = ingredient.nutrientsPer_100g[infood];
  //       if (value) {
  //         nutrients[infood] += value;
  //       }
  //     }
  //   }

  //   for (let infood of Object.keys(targets)) {
  //     let target = targets[infood];
  //     let value = nutrients[infood];
  //     if (target.min && value < target.min) {
  //       let a = 0;
  //     }
  //     if (target.max && value < target.max) {
  //       let a = 0;
  //     }
  //   }
  // }

  public solve(paramsOrg: MealTunerParams): MealTunerResults {
    // declared out here, outside the try, so the catch below can still put it in the failure it
    // returns. nothing writes to it today -- it is a debugging hook the result shape carries.
    const debug = '';
    try {
      const params = deepClone(paramsOrg);
      const targets = params.targets;

      // read the calorie target through `?.` because the tag can be absent altogether, not just
      // empty: `actualMealTargets` DELETES a nutrient tag outright once its min, max and target
      // have all fallen out, and that happens to calories on a day whose other meals have already
      // used up the day's maximum. reading `.target` off the missing tag threw a TypeError, which
      // the catch below turned into a message about reading a property of undefined -- true, and
      // useless to whoever reads it. `isFinite(undefined)` is false, so the guard already sitting
      // here answers that case with its own words instead.
      const targetEnergy = targets.ENER$_kcal?.target;
      if (!isFinite(targetEnergy) || targetEnergy < 1) {
        throw Error('Target energy not specified');
      }

      let protTarget = targets[INFOODS.Protein];
      if (protTarget && protTarget.min && !protTarget.max) {
        protTarget.max = protTarget.min * 2;
      }

      // calculate maximum weight for all relevant nutrients
      let totalWeight = 0;
      for (let targetInfood of Object.keys(targets)) {
        let target = targets[targetInfood];
        totalWeight += (target.target || target.max || target.min || 0) * (infoodImportanceWeights[targetInfood] || 1);
      }

      const context: MealSolveContext = {
        foods: params.foods,
        targets,
        targetEnergy,
        totalTargetWeight: totalWeight,
      };

      const result: MealTunerSolved = {
        outcome: MealTunerOutcome.Solved,
        debug,
        params: paramsOrg,
        meals: [],
      };
      const invalidMeals: MealTunerInvalidMeal[] = [];
      for (let [mealIndex, meal] of params.meals.entries()) {
        // asked BEFORE tuning, not after a failed scale, because a broken number does not reliably
        // stop the scaling. a missing amount or a broken grams-per-measure poisons the calorie sum
        // with NaN and so does stop it -- but a recipe stored with a total weight of 0 arrives with
        // Infinity or NaN in every nutrient, `deepClone` DROPS those, and the missing calories come
        // back as a clean 0 through `|| 0`. The meal then scales perfectly well on whatever OTHER
        // food it holds, and the user is handed amounts computed for a meal that is not the one
        // they logged (df-e0ab24af4482). Asking first is what makes those two cases behave alike.
        //
        // It is asked of the caller's ORIGINAL meal, never the copy, because the copy is exactly
        // where the broken numbers have already been lost.
        const unusableNumber = this.findUnusableNumber(paramsOrg.meals[mealIndex], paramsOrg.foods, targets);
        if (unusableNumber) {
          invalidMeals.push({ id: meal.id, detail: unusableNumber });
          continue;
        }
        let tunedMeal = this.solveOneMeal(meal, context);
        if (tunedMeal) {
          result.meals.push(tunedMeal);
          continue;
        }
        // the meal's numbers are all usable and it still cannot be scaled, so nothing it may
        // resize carries calories: every row is locked or typed in, the free rows are calorie-free
        // food, or the meal is empty. that is a real state of the user's meal rather than a fault,
        // and the callers explain it to the user themselves.
      }

      // a meal with a broken number is a fault in the data, not an honest "this cannot be
      // tuned", so it is listed for the caller to report rather than dropped silently.
      //
      // if the solve was asked to tune the user's own meal and that meal is broken, the request
      // itself failed, so it answers with the FAILED shape, which every caller already treats as
      // a crash. a solve over candidates (`candidates`) only lists its broken ones, however few
      // there were: it has answered "none of these is usable", which its callers report as broken
      // candidates rather than as a crash, so it stays on the solved shape.
      if (invalidMeals.length > 0) {
        if (!params.candidates) {
          return {
            outcome: MealTunerOutcome.Failed,
            error: `meal ${invalidMeals[0].id} has a number the tuner cannot use: ${invalidMeals[0].detail}`,
            debug,
            params: paramsOrg,
          };
        }
        result.invalidMeals = invalidMeals;
      }

      result.meals.sort((a, b) => {
        if (a.precision_pc > b.precision_pc) return -1;
        if (a.precision_pc < b.precision_pc) return 1;
        return 0;
      });

      return result;
    } catch (error) {
      // The FAILED shape is what tells a caller this was an internal crash rather than the honest
      // "no combination of these foods fits". It carries no `meals` at all, so a caller cannot
      // reach an answer without narrowing on `outcome` first -- which is what stops a crash being
      // read as an ordinary negative result (#5297).
      //
      // The `||` keeps "a failure always describes itself" true rather than nearly true.
      // `describeError` hands a thrown STRING straight back, so `throw ''` would otherwise come
      // out as `''`. Nothing in here throws a bare string today; the fallback is here so that a
      // caller putting this message in front of a person, or in a Sentry issue title, always has
      // words to show.
      //
      // Both helpers are used, each on its own side -- do NOT tidy this into one call. The
      // log line gets `describeErrorForLog`, which carries the stack; the result object gets
      // `describeError`, which never does, because callers put that field in Sentry issue
      // titles. The rule is in `design-docs/typescript-patterns.md`.
      //
      // The console line is the ONLY thing that recovers the throw site: every caller raises a
      // Sentry event right after reading `error`, and on the client every console call becomes a
      // breadcrumb on it. Without it a tuner crash names the site that reported it, never the
      // line that threw.
      console.error(`MealTuner.solve failed: ${describeErrorForLog(error)}`);

      return {
        outcome: MealTunerOutcome.Failed,
        error: describeError(error) || 'MealTuner.solve failed with no description',
        debug,
        params: paramsOrg,
      };
    }
  }
  /**
   * Say which number in this meal the tuner cannot use, or answer null when every one is usable.
   * Asked about EVERY meal before it is tuned, and always about the caller's ORIGINAL params rather
   * than the copy `solve` works from -- the call site explains both.
   *
   * What is checked: a row's amount, its grams per measure, and the TARGETED nutrients of the food
   * behind it -- every nutrient this solve has a target for, which is exactly the set the tuner
   * reads. Wider than the calories alone, because a recipe whose stored calories are absent or 0
   * while its protein came out as Infinity is just as unusable, and used to pass a calorie-only
   * check. Deliberately no wider than that: a stored nutrient map can carry a damaged value in any
   * tag (see `Nutrients` in `data-model/food.ts`), and refusing to tune a meal over a micronutrient
   * nothing here ever reads would take away a correct answer for no gain.
   *
   * A missing amount counts as broken whether it arrives as undefined or as null (a NaN weight is
   * saved as null, and the built-in `isFinite(null)` is true). A missing nutrient value is allowed
   * -- the tuner reads it as 0.
   */
  private findUnusableNumber(meal: MealTunerMeal, foods: MealTunerFoodMap, targets: NutrientTargets): string | null {
    const targetedNutrients = Object.keys(targets ?? {});
    for (let [ingredientIndex, ingredient] of (meal.ingredients ?? []).entries()) {
      if (ingredient.foodId === MACRO_INGREDIENT_FOOD_ID) {
        const unusableNutrient = this.findUnusableNutrient(ingredient.nutrients, targetedNutrients);
        if (unusableNutrient) {
          return `typed-in ingredient ${ingredientIndex} has ${unusableNutrient}`;
        }
        continue;
      }
      if (!isUsableAmount(ingredient.measures) || !isUsableAmount(ingredient.gramsPerMeasure)) {
        return `ingredient ${ingredientIndex} (${ingredient.foodId}) has measures ${ingredient.measures} and grams per measure ${ingredient.gramsPerMeasure}`;
      }
      const unusableNutrient = this.findUnusableNutrient(foods?.[ingredient.foodId]?.nutrientsPer_100g, targetedNutrients);
      if (unusableNutrient) {
        return `ingredient ${ingredientIndex} (${ingredient.foodId}) has ${unusableNutrient} per 100 g`;
      }
    }
    return null;
  }

  /**
   * Name the first of `nutrientTags` whose value in this map the tuner cannot use, as
   * `<tag> <value>`, or answer null when every one is usable. The tag goes in the text because the
   * message is read in a Sentry issue by somebody who cannot see the document.
   *
   * It walks the TAGS rather than the map's own keys, so a damaged value in a nutrient this solve
   * has no target for is passed over -- nothing in the tuner reads it. The caller decides which
   * tags those are.
   *
   * An absent value and a value of `null` are both allowed: the tuner reads a nutrient it was given
   * no number for as 0. Only a number that is present and not finite is a fault -- and `null` has
   * to be excluded by hand, because the built-in `isFinite(null)` is true.
   */
  private findUnusableNutrient(nutrients: Nutrients | undefined, nutrientTags: string[]): string | null {
    for (const nutrientTag of nutrientTags) {
      const value = nutrients?.[nutrientTag];
      if (value != null && !isFinite(value)) {
        return `${nutrientTag} ${value}`;
      }
    }
    return null;
  }

  /**
   * Tune one meal, and hand back a changed COPY of it -- the caller's own meal is never touched.
   *
   * A solve runs as a sequence of stages, and their order is not free. Each one below is written
   * in the order it must run, and takes what it needs as an argument, so what a stage depends on
   * can be read off its signature instead of worked out from the whole method.
   *
   * Two of the stages read a field that an earlier stage REWROTE, and that is the one thing here
   * which no signature can state:
   *
   *  - `chooseFractionBand` may give up on counting a row in fractions. When it does, it deletes
   *    that row's `fractions` and converts the row to grams: it multiplies `measures` by
   *    `gramsPerMeasure` and then sets `gramsPerMeasure` to 1. So from that point on, one
   *    "measure" of that row means one GRAM, whatever it meant at the top of this method.
   *  - `readBackSolvedAmounts` divides the solver's answer by `fractions`, which the stage above
   *    may have removed, and `recomputeNutrients` multiplies `gramsPerMeasure` by the solved
   *    amount to get grams.
   *
   * Those rewrites are part of the ANSWER rather than scratch space, so they must not be tidied
   * away: the tuned meal is handed back to the caller, and a caller works out a row's weight as
   * `tunedMeasures * gramsPerMeasure` (see `client/src/services/meal-tuner-rebuild.ts`).
   */
  private solveOneMeal(meal: MealTunerMeal, context: MealSolveContext): MealTunerMeal {
    const tunedMeal = this.copyMealForTuning(meal);

    const heldRows: HeldRows = new Map();
    const scaledWeight = this.scaleToCalorieTarget(tunedMeal, context, heldRows);
    if (scaledWeight === null) {
      return null; // nothing to resize
    }

    const model = this.buildModel(tunedMeal, context, scaledWeight, heldRows);

    let results: SolverResults = solver.Solve(model);

    if (!results.feasible) {
      return null;
    }

    const internalPrecision_pc = this.precisionTheSolverReports(results, context);

    this.readBackSolvedAmounts(tunedMeal, results, heldRows);

    // verification...
    const nutrients = this.recomputeNutrients(tunedMeal, context);

    let match = calculateNutrientMatch_pc(nutrients, context.targets);
    if (Math.abs(match - internalPrecision_pc) > 2) {
      console.log(`Auto turner precision fail for meal ${tunedMeal.id}: ${Math.abs(match - internalPrecision_pc)} %`);
    }

    tunedMeal.precision_pc = match;
    tunedMeal.nutrients = nutrients;

    return tunedMeal;
  }

  /**
   * Copy the caller's meal down to the fields a solve reads and writes.
   *
   * Everything below this point writes into the copy, which is what keeps the promise that the
   * caller's own meal is left exactly as it was.
   */
  private copyMealForTuning(meal: MealTunerMeal): MealTunerMeal {
    return {
      id: meal.id,
      ingredients: meal.ingredients.map(i => ({
        foodId: i.foodId,
        gramsPerMeasure: i.gramsPerMeasure,
        measures: i.measures,
        fractions: i.fractions,
        locked: i.locked,
        nutrients: i.nutrients,
      })),
    };
  }

  /**
   * Scale every row the tuner may resize, so that the meal as a whole lands on the calorie target,
   * and answer with what the meal weighs after that scaling.
   *
   * This stage WRITES into the meal as well as returning a value. It sets `tunedMeasures` on every
   * row, which is what the model built after it reads, and `rescaled` on the meal, which nothing
   * here reads at all -- that one is carried out to the caller as part of the answer.
   *
   * It also fills `heldRows` with every row it leaves unscaled, and the amount each is held at.
   * The model and the read-back both depend on that list -- see `HeldRows`.
   *
   * Answers `null` when the meal cannot be scaled at all. That is not the same as a weight of 0,
   * so the caller compares against `null` rather than testing for a falsy number.
   */
  private scaleToCalorieTarget(tunedMeal: MealTunerMeal, context: MealSolveContext, heldRows: HeldRows): number | null {
    for (let ingredient of tunedMeal.ingredients) {
      if (ingredient.foodId === MACRO_INGREDIENT_FOOD_ID) {
        continue;
      }
      if (ingredient.locked || (isCountedInWholeUnits(ingredient) && ingredient.measures < 1)) {
        heldRows.set(ingredient, ingredient.measures);
      }
    }

    // a scale of zero or below would shrink the free rows to nothing or less. holding the eggs can
    // cause that when the held eggs alone carry more calories than the meal's target, so it is
    // treated like having nothing to resize.
    const scalesTheMeal = (totals: ScaledTotals | null) => totals !== null && totals.scale > 0;

    let totals = this.scaleHoldingWholeUnitsAtOneOrMore(tunedMeal, context, heldRows);
    if (!scalesTheMeal(totals)) {
      // if holding the whole-unit rows left nothing with calories to resize -- a meal of nothing
      // but eggs, say -- release them and scale the way the tuner did before it held them. the
      // user then gets the old answer (the egg rounded to one) rather than being told the meal
      // cannot be tuned at all, which would be the wrong thing to tell them.
      //
      // the rows held at one unit are released first, and a row held at a recipe amount below one
      // only if that still leaves nothing to resize: half an egg beside two eggs keeps its half.
      // a row the user locked is never released.
      this.releaseHeldRows(heldRows, ingredient => !ingredient.locked && ingredient.measures >= 1);
      totals = this.scaleWithHeldRows(tunedMeal, context, heldRows);
      if (!scalesTheMeal(totals)) {
        this.releaseHeldRows(heldRows, ingredient => !ingredient.locked);
        totals = this.scaleWithHeldRows(tunedMeal, context, heldRows);
      }
    }
    // if this is still null the meal cannot be scaled, and by the time a meal reaches here `solve`
    // has already proven that none of its numbers is broken -- so the rows the tuner may resize
    // carry no calories at all: every row is locked or typed in, the free rows are calorie-free
    // food, or the meal is empty. that is a real state of the user's meal rather than a fault, and
    // the callers explain it to the user themselves.
    if (totals === null) {
      return null; // nothing to resize
    }

    const { scale, heldWeight, freeWeight } = totals;
    tunedMeal.rescaled = scale;
    for (let ingredient of tunedMeal.ingredients) {
      if (ingredient.foodId === MACRO_INGREDIENT_FOOD_ID) {
        ingredient.tunedMeasures = 1;
        continue;
      }
      if (heldRows.has(ingredient)) {
        ingredient.tunedMeasures = heldRows.get(ingredient);
        continue;
      }
      ingredient.tunedMeasures = ingredient.measures * scale;
    }
    return heldWeight + freeWeight * scale;
  }

  /** Take every held row that `shouldRelease` picks out of `heldRows`, so the tuner resizes it again. */
  private releaseHeldRows(heldRows: HeldRows, shouldRelease: (ingredient: MealTunerIngredient) => boolean): void {
    for (let ingredient of [...heldRows.keys()]) {
      if (shouldRelease(ingredient)) {
        heldRows.delete(ingredient);
      }
    }
  }

  /**
   * Scale the meal with the rows already in `heldRows` fixed, and keep holding every whole-unit row
   * the scale would take below one unit, at exactly one unit, until none is left.
   *
   * Holding a row changes the scale, and a smaller scale can take another whole-unit row below one
   * unit -- which is why this repeats. Rows are only ever added, and each one holds MORE than
   * scaling gave it, so the scale only shrinks and this ends after at most one pass per row.
   *
   * Answers null, like `scaleWithHeldRows`, when no row left free carries any calories.
   */
  private scaleHoldingWholeUnitsAtOneOrMore(tunedMeal: MealTunerMeal, context: MealSolveContext, heldRows: HeldRows): ScaledTotals | null {
    while (true) {
      const totals = this.scaleWithHeldRows(tunedMeal, context, heldRows);
      if (totals === null) {
        return null;
      }
      let heldAnother = false;
      for (let ingredient of tunedMeal.ingredients) {
        if (ingredient.foodId === MACRO_INGREDIENT_FOOD_ID || heldRows.has(ingredient)) {
          continue;
        }
        if (isCountedInWholeUnits(ingredient) && ingredient.measures * totals.scale < 1) {
          heldRows.set(ingredient, 1);
          heldAnother = true;
        }
      }
      if (!heldAnother) {
        return totals;
      }
    }
  }

  /**
   * Work out the one scale factor for every row NOT in `heldRows` that brings the meal to the
   * calorie target, with each held row counted at its held amount and every typed-in row as given.
   *
   * Answers null when that scale is not a finite number, which means the free rows carry no
   * calories at all.
   */
  private scaleWithHeldRows(tunedMeal: MealTunerMeal, context: MealSolveContext, heldRows: HeldRows): ScaledTotals | null {
    let heldEnergy = 0;
    let freeEnergy = 0;
    let heldWeight = 0;
    let freeWeight = 0;
    for (let ingredient of tunedMeal.ingredients) {
      if (ingredient.foodId === MACRO_INGREDIENT_FOOD_ID) {
        heldEnergy += ingredient.nutrients.ENER$_kcal || 0;
        continue;
      }
      let food = context.foods[ingredient.foodId];
      let ingredientEnergyPer_100g = food.nutrientsPer_100g.ENER$_kcal || 0;
      const held = heldRows.has(ingredient);
      const measures = held ? heldRows.get(ingredient) : ingredient.measures;
      const ingredientEnergy = measures * ingredientEnergyPer_100g * (ingredient.gramsPerMeasure / 100);
      const ingredientWeight = measures * ingredient.gramsPerMeasure;
      if (held) {
        heldEnergy += ingredientEnergy;
        heldWeight += ingredientWeight;
      } else {
        freeEnergy += ingredientEnergy;
        freeWeight += ingredientWeight;
      }
    }
    const scale = (context.targetEnergy - heldEnergy) / freeEnergy;
    if (!isFinite(scale)) {
      return null;
    }
    return { scale, heldWeight, freeWeight };
  }

  /**
   * Build the linear program for one already-scaled meal: the nutrient rows first, then one row
   * per ingredient.
   */
  private buildModel(tunedMeal: MealTunerMeal, context: MealSolveContext, scaledWeight: number, heldRows: HeldRows): SolverModel {
    const model: SolverModel = {
      // we are trying to minimize the amount of cost which is weighted "error" for all nutrients
      optimize: 'cost',
      opType: 'min',
      constraints: {},
      variables: {},
      binaries: {},
      ints: {},
    };

    this.addNutrientTargetConstraints(model, context);
    this.addIngredientConstraints(model, tunedMeal, context, scaledWeight, heldRows);

    return model;
  }

  /** Add the rows that price a meal's distance from each nutrient target. */
  private addNutrientTargetConstraints(model: SolverModel, context: MealSolveContext): void {
    /* each nutrient is constrained by this formula :
      
      infood_total = SUM(infood_for_ingredient * ingredient_amount) - (infood_free - infood_under + infood_over)

      where:
        * infood is the INFOODs tag for that nutrient
        * infood_total is constrained to be === 0
        * the sign of the row is set so that sums for ingredients are positive, but then the under/over have to be flipped. 
            beware of that detail in the implementation
        * infood_free is constrained to be between min and max for that ingredient - and doesn't contribute to the cost
        * infood_under and infood_over are >=0 and costed as desired
        * infood_under exists only if limited by minimum
        * infood_over exists only if limited by maximum

      */
    for (let infood of Object.keys(context.targets)) {
      let target = context.targets[infood];

      model.constraints[infood + '_total'] = { equal: 0 };

      model.variables[infood + '_free'] = { [infood + '_total']: -1, [infood + '_limits']: 1 };

      let tagWeight = infoodImportanceWeights[infood] || 1;

      let constraint = (model.constraints[infood + '_limits'] = {});
      if (target.min !== undefined) {
        constraint['min'] = target.min;
        model.variables[infood + '_under'] = { [infood + '_total']: 1, ['cost']: tagWeight };
      }
      if (target.max !== undefined) {
        constraint['max'] = target.max;
        model.variables[infood + '_over'] = { [infood + '_total']: -1, ['cost']: tagWeight };
      }
    }
  }

  /**
   * Add one group of rows per ingredient: what it may weigh, what changing it costs, and what it
   * contributes to each nutrient.
   */
  private addIngredientConstraints(
    model: SolverModel,
    tunedMeal: MealTunerMeal,
    context: MealSolveContext,
    scaledWeight: number,
    heldRows: HeldRows,
  ): void {
    /* each ingredient is constrained by this:

    ingredient_actual - ingredient_inc + ingredient_dec = ingredient_default

    ( note that this is equivalent to:
       ingredient_actual  = ingredient_default + ingredient_inc - ingredient_dec
    )

    where:
      * ingredient is this ingredient's model key -- its position in the meal, see
        modelKeyForIngredient. it is NOT the food's id: two ingredients can share a food.
      * ingredient_default is constrained to be == the amount after initial scaling
      * ingredient_actual is the resulting variable - it is hard limited by allowed change (+/- 20%)
      * ingredient_inc and ingredient_dec are used to add a small cost of changing the food, so that the
        system avoids moving foods that don't have much influence (extreme example: water)
    */

    for (let ingredientIndex = 0; ingredientIndex < tunedMeal.ingredients.length; ingredientIndex++) {
      let ingredient = tunedMeal.ingredients[ingredientIndex];
      let foodId = ingredient.foodId;
      let ingredientKey = modelKeyForIngredient(ingredientIndex);
      let foodCost = foodId !== MACRO_INGREDIENT_FOOD_ID ? (scaledWeight / (ingredient.gramsPerMeasure * ingredient.tunedMeasures)) * 0.01 : 0;

      model.variables[ingredientKey + '_actual'] = { [ingredientKey + '_limits']: 1, [ingredientKey + '_default']: 1 };
      model.variables[ingredientKey + '_inc'] = { ['cost']: foodCost, [ingredientKey + '_default']: -1 };
      model.variables[ingredientKey + '_dec'] = { ['cost']: foodCost, [ingredientKey + '_default']: 1 };
      // the band the solver may move this row within: plus or minus 20% of the scaled amount.
      let minMeasures = ingredient.tunedMeasures * 0.8;
      let maxMeasures = ingredient.tunedMeasures * 1.2;

      const held = heldRows.has(ingredient);
      if (ingredient.fractions && !held) {
        const band = this.chooseFractionBand(ingredient, minMeasures, maxMeasures);
        minMeasures = band.minMeasures;
        maxMeasures = band.maxMeasures;

        // a row still counted in fractions may only take whole fraction steps, so its amount is an
        // integer in the model. a row whose fractions the stage above gave up on is free again.
        if (ingredient.fractions) {
          model.ints[ingredientKey + '_actual'] = 1;
        }
      }

      let ingredientFractions = ingredient.fractions || 1;
      if (held) {
        minMeasures = ingredient.tunedMeasures;
        maxMeasures = ingredient.tunedMeasures;
        ingredientFractions = 1;
      } 
      model.constraints[ingredientKey + '_limits'] = {
        min: minMeasures,
        max: maxMeasures,
      };

      model.constraints[ingredientKey + '_default'] = {
        equal: ingredient.tunedMeasures * ingredientFractions,
      };
      for (let infood of Object.keys(context.targets)) {
        if (foodId === MACRO_INGREDIENT_FOOD_ID) {
          const nutrient = ingredient.nutrients[infood];
          model.variables[ingredientKey + '_actual'][infood + '_total'] = nutrient || 0;
        } else {
          let nutrientPer100g = context.foods[foodId].nutrientsPer_100g[infood];
          if (nutrientPer100g !== undefined) {
            model.variables[ingredientKey + '_actual'][infood + '_total'] =
              ((nutrientPer100g / 100) * ingredient.gramsPerMeasure) / (ingredientFractions);
          }
        }

      }
    }
  }

  /**
   * Work out the allowed amount band for ONE row that is counted in fractions -- halves, quarters,
   * or whole units -- given the plus-or-minus-20% band it would have had as a free amount.
   *
   * It tries each fraction count in turn and keeps the first whose rounded band still sits inside
   * that original band. If none does, it gives up on fractions for this row altogether.
   *
   * 🛑 IT ALSO REWRITES THE ROW, and later stages depend on that, so the writes are not incidental:
   *
   *  - `ingredient.fractions` is set to whichever count it settled on, and DELETED if it gave up.
   *    `readBackSolvedAmounts` divides the solver's answer by this.
   *  - when it gives up, the row is converted to grams -- `measures` is multiplied by
   *    `gramsPerMeasure`, and `gramsPerMeasure` becomes 1. From then on "one measure" of this row
   *    means one gram, both in the returned band and everywhere below. That is why the two
   *    give-up lines multiply the band by `gramsPerMeasure` while the fraction lines multiply by
   *    the fraction count: they are answering in two different units.
   *
   * The row is handed back to the caller of the tuner, so these rewrites are part of the answer.
   */
  private chooseFractionBand(
    ingredient: MealTunerIngredient,
    minMeasuresLimit: number,
    maxMeasuresLimit: number,
  ): IngredientAmountBand {
    let minMeasures = minMeasuresLimit;
    let maxMeasures = maxMeasuresLimit;

    let start = 2;
    let end = 4;
    let step = 2;
    if (ingredient.fractions === 1) {
      start = 1;
      end = 1;
      step = 1;
    }
    for (let fractions = start; fractions <= end; fractions += step) {
      ingredient.fractions = fractions;
      minMeasures = minMeasuresLimit * ingredient.fractions;
      maxMeasures = maxMeasuresLimit * ingredient.fractions;

      minMeasures = Math.ceil(minMeasures);
      maxMeasures = Math.floor(maxMeasures);
      if (maxMeasures < minMeasures) {
        maxMeasures = minMeasures;
        if (minMeasures > 1) {
          minMeasures--;
        }
      }
      if (
        minMeasures / ingredient.fractions >= minMeasuresLimit &&
        minMeasures / ingredient.fractions <= maxMeasuresLimit &&
        maxMeasures / ingredient.fractions >= minMeasuresLimit &&
        maxMeasures / ingredient.fractions <= maxMeasuresLimit
      ) {
        break;
      } else {
        if (fractions === 4) {
          delete ingredient.fractions;
          minMeasures = minMeasuresLimit * ingredient.gramsPerMeasure;
          maxMeasures = maxMeasuresLimit * ingredient.gramsPerMeasure;
          ingredient.measures *= ingredient.gramsPerMeasure;
          ingredient.gramsPerMeasure = 1;
          break;
        }
      }
      // if (maxMeasures > maxMeasuresLimit) {
      //   return null;
      // }
    }

    return { minMeasures, maxMeasures };
  }

  /**
   * How close the solver itself says it got, as a percentage: the weighted total of everything it
   * had to leave under or over a target, against the weight of all the targets together.
   *
   * `solveOneMeal` recomputes the same figure from the tuned meal afterwards and logs it when the
   * two disagree by more than 2 points, which is what catches a model that no longer describes
   * the meal it was built from.
   */
  private precisionTheSolverReports(results: SolverResults, context: MealSolveContext): number {
    let totalError = 0;
    for (let tag of Object.keys(context.targets)) {
      let err = (results[tag + '_under'] || 0) + (results[tag + '_over'] || 0);
      totalError += err * (infoodImportanceWeights[tag] || 1);
    }
    return (1 - totalError / context.totalTargetWeight) * 100;
  }

  /**
   * Read the solved amount of every row the tuner resized (every row not in `heldRows`) out of the
   * solver's answer and round it, writing each one back onto that row as `tunedMeasures`.
   *
   * Reads `fractions` and `gramsPerMeasure` as `chooseFractionBand` left them -- see the note on
   * `solveOneMeal`.
   */
  private readBackSolvedAmounts(tunedMeal: MealTunerMeal, results: SolverResults, heldRows: HeldRows): void {
    for (let ingredientIndex = 0; ingredientIndex < tunedMeal.ingredients.length; ingredientIndex++) {
      let ingredient = tunedMeal.ingredients[ingredientIndex];
      if (heldRows.has(ingredient)) {
        continue;
      }
      // read this ingredient's own solved amount back. it has to be looked up by the same
      // per-ingredient key the model was built with -- looking it up by food id would hand
      // two entries of one food the same amount.
      ingredient.tunedMeasures = results[modelKeyForIngredient(ingredientIndex) + '_actual'] / (ingredient.fractions || 1);
      // round to sensible values - users don't need to see 201.344 grams
      if (ingredient.gramsPerMeasure === 1) {
        if (ingredient.tunedMeasures < 3) {
          ingredient.tunedMeasures = roundToPrecision(ingredient.tunedMeasures, 0.1);
        } else {
          ingredient.tunedMeasures = Math.round(ingredient.tunedMeasures);
        }
      }
    }
  }

  /**
   * Add up what the meal actually holds, now that every row carries its solved and rounded amount.
   *
   * This is the check on the solver's own answer rather than a repeat of it: the rounding above
   * has moved the amounts since the solver produced them, so this is what the caller is told the
   * meal contains.
   */
  private recomputeNutrients(tunedMeal: MealTunerMeal, context: MealSolveContext): Nutrients {
    let nutrients: Nutrients = {};
    for (let tag of Object.keys(context.targets)) {
      nutrients[tag] = 0;
    }

    for (let ingredient of tunedMeal.ingredients) {
      if (ingredient.foodId === MACRO_INGREDIENT_FOOD_ID) {
        for (let tag of Object.keys(context.targets)) {
          nutrients[tag] += ingredient.nutrients[tag] || 0;
        }
      } else {
        let ingredientNutrients = context.foods[ingredient.foodId].nutrientsPer_100g;
        let grams = ingredient.gramsPerMeasure * ingredient.tunedMeasures;
        for (let tag of Object.keys(context.targets)) {
          nutrients[tag] += ((ingredientNutrients[tag] || 0) / 100) * grams;
        }
      }
    }

    return nutrients;
  }
}
